import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Patient } from "../model/patient.";
import { ObservationLabsService } from "./observation.labs.service";
import { UtilityService } from "../shared/service/utility.service";
import { mergeMap, expand, map, reduce, finalize } from "rxjs/operators";
import { EMPTY, forkJoin, from } from "rxjs";
import { AppConfig } from "../shared/constants/app.config";
import { HttpClient } from "@angular/common/http";
import { SpinnerService } from "../spinner/spinner.service";
import { ActivatedRoute } from "@angular/router";
import { Sort } from "@angular/material/sort";
import { AngularAppLogger } from "../angular-app-logger";


@Component({
    selector: "app-observation.labs",
    templateUrl: "./observation.labs.component.html",
    styleUrls: ["./observation.labs.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ObservationLabsComponent implements OnInit {
  labs: any;
  priority: any;
  now: Date;
  startDate: Date;
  endDate: Date;
  selectedNewEntries = [];
  selectedUpdatedEntries = [];
  selectedNewResources = [];
  selectedUpdatedResources = [];
  psScope: string;
  ehrpatient: Patient;
  isAllSelected: boolean;
  isAlldisabled: boolean;
  totalSuccessCount: number;
  totalFailCount: number;
  categoryData: any = [];
  selectedOrg: any;
  crid: string;
  isPriorityLabs: boolean;

  constructor(
    readonly http: HttpClient,
    public observationlabsService: ObservationLabsService,
    readonly utility: UtilityService,
    readonly spinner: SpinnerService,
    readonly route: ActivatedRoute,
    readonly appLogger: AngularAppLogger
  ) {
    const data = utility.data;

    if (data.priority) {
      this.priority = this.utility.bundleResources(data.priority).entry;
      this.appLogger.logInfo(`${this.priority.length} priority labs observations fetched from EHR`, { context: `Logged from Obs Labs component`, allowDuplicate: true });
    }

    if (data.labs) {
      this.labs = this.utility.bundleResources(data.labs).entry;
      this.appLogger.logInfo(`${this.labs.length} all-labs observations fetched from EHR`, { context: `Logged from Obs Labs component`, allowDuplicate: true });
    }
    this.crid = data.crid;
    this.selectedOrg = data.selectedOrg;
    this.psScope = this.selectedOrg.value;
  }

  ngOnInit() {
    this.categoryData = this.getCategoryData();
    this.sortHeader({ active: "time", direction: "desc" });
    this.now = new Date();
    this.startDate = this.utility.data.startDate ?? new Date(new Date().setFullYear(new Date().getFullYear() - 2));
    this.endDate = this.utility.data.endDate ?? new Date();

    const telecomUrlItems = this.selectedOrg.telecomUrlItems;

    if (telecomUrlItems && telecomUrlItems.length > 0) {
      const urls = telecomUrlItems.map((item) => item.value);
      forkJoin(urls.map((url) => this.getCibmtrLabs(url)));
    } else {
      this.getCibmtrLabs(localStorage.getItem("iss"));
    }
  }

  getCibmtrLabs(url) {
    if (localStorage.getItem("fhir_version")?.includes("STU3")) {
      this.getCibmtrObs(url, this.selectedOrg.value, AppConfig.CIBMTR_FHIR_R3_URL);
    } else {
      this.getCibmtrObs(url, this.selectedOrg.value, AppConfig.CIBMTR_FHIR_R4_URL);
      const modifiedUrl = localStorage.getItem("iss")?.includes('logicahealth') ?
        url.toString().replace(AppConfig.LOGICA_EHRS[1], AppConfig.LOGICA_EHRS[0])
        : url.toString().replace("R4", "STU3");
      this.getCibmtrObs(modifiedUrl, this.selectedOrg.value, AppConfig.CIBMTR_FHIR_R3_URL)
    }
  }

  getCibmtrObs(url: string, selectedOrgVal, fhirUrl: string): void {
    this.spinner.start();
    this.observationlabsService
      .getCibmtrObservationsLabs(url, selectedOrgVal, fhirUrl)
      .pipe(
        expand((response) => {
          const next =
            response.link && response.link.find((l: any) => l.relation === "next");
          if (next) {
            const modifiedUrl =
              `${fhirUrl}?${next.url.split("?")[1]}`;
            return this.http.get(modifiedUrl);
          } else {
            return EMPTY;
          }
        }),
        map((response: any) => {
          if (response.entry) {
            return response.entry.flatMap((array) => array);
          }
          return [];
        }),
        reduce((acc, x) => acc.concat(x), [])
      )
      .subscribe(
        (savedEntries) => {
          this.updateObservationEntries(url, savedEntries);
          if (savedEntries.length > 0) {
            this.appLogger.logInfo(`${savedEntries.length} previously saved Total Observations found`, { context: 'Retrieval of saved CIBMTR Obs', url: fhirUrl, allowDuplicate: true });
          }
          this.spinner.end();
        },
        (error) => {
          this.spinner.end();
          let errorMsg = "Error occurred while fetching saved observations. Check message tray for details.";
          this.appLogger.logError(errorMsg, {
            url: fhirUrl,
            crid: this.crid,
            startDate: this.utility.data.startDate,
            endDate: this.utility.data.endDate
          });
          this.appLogger.logError(error, { component: 'ObservationLabsComponent', context: 'Fetch saved observations from Cibmtr' });
        }
      );
  }

  private updateObservationEntries(url: any, savedEntries: any[]) {
    const entries = this.categoryData;
    if (entries?.length > 0) {
      //Filters Operationoutcome entries
      const observationEntries = entries.filter(function (item: any) {
        return item.resource.resourceType === "Observation";
      });

      for (const observationEntry of observationEntries) {
        this.updateObsSubjReference(observationEntry, url);
        // Case I - Default state the record has not been submitted
        if (!observationEntry.state) {
          observationEntry.state = "bold";
          observationEntry.selected = false;
        }

        if (savedEntries?.length > 0) {
          // Case II - The record has been submitted and there were no updates
          // we cannot submit these records unless there is a change in data.
          // sse stands for submitted  saved entry
          const sse = this.populateSubmittedSavedEntries(savedEntries, observationEntry);

          // use - updated saved entry
          const use = this.populateUpdatedSavedEntries(savedEntries, observationEntry);

          if (sse.length > 0) {
            observationEntry.selected = true;
            observationEntry.state = "lighter";
            observationEntry.resource.id = sse[0].resource.id;
          }

          // Case III - The record has been submitted and there were updates made after
          else if (use.length > 0) {
            observationEntry.state = "normal";
            observationEntry.resource.id = use[0].resource.id;
          }
        }
      }
    }
  }

  private populateSubmittedSavedEntries(savedEntries: any[], observationEntry: any) {
    return savedEntries.filter((savedEntry) => {
      const createdId = savedEntry.resource.identifier[0].value.match(
        new RegExp("/Observation/(.*)")
      )[1];

      return (
        savedEntry.resource.identifier &&
        observationEntry.resource.id === createdId &&
        observationEntry.resource.issued === savedEntry.resource.issued
      );
    });
  }

  private populateUpdatedSavedEntries(savedEntries: any[], observationEntry: any) {
    return savedEntries.filter((savedEntry) => {
      const updatedId = savedEntry.resource.identifier[0].value.match(
        new RegExp("/Observation/(.*)")
      )[1];

      return (
        savedEntry.resource.identifier &&
        observationEntry.resource.id === updatedId &&
        observationEntry.resource.issued !== savedEntry.resource.issued
      );
    });
  }

  getNodeValue(value: any) {
    if (value) {
      if (this.isValueCodeableConcept(value)) {
        return value.valueCodeableConcept.coding[0].code;
      } else if (this.isValueQuantity(value)) {
        return this.getValueQuantityUnitOrValue(value.valueQuantity);
      } else if (value.valueDateTime) {
        return value.valueDateTime;
      } else if (value.valueString) {
        return value.valueString;
      } else if (value.valueTime) {
        return value.valueTime;
      } else if (value.valueBoolean) {
        return value.valueBoolean;
      }
    }
    return null;
  }

  getValueQuantityUnitOrValue(valueQuantity: any) {
    if (valueQuantity.value && valueQuantity.unit) {
      return `${valueQuantity.value} ${valueQuantity.unit}`;
    } else if (valueQuantity.value) {
      return valueQuantity.value;
    } else {
      return null;
    }
  }

  isValueQuantity(value: any) {
    return value.valueQuantity &&
      (value.valueQuantity.value || value.valueQuantity.unit);
  }

  isValueCodeableConcept(value: any) {
    return value.valueCodeableConcept &&
      value.valueCodeableConcept.coding[0] &&
      value.valueCodeableConcept.coding[0].code;
  }

  updateObsSubjReference(observation: any, url: string) {
    if (observation.fullUrl.toString().includes(url.toString()) && !(observation.resource.subject.reference.toString().includes('http'))) {
      observation.resource.subject.reference = `${url}/${observation.resource.subject.reference}`;
    }
  }
  //code
  getComponentValue(component: any) {
    if (component) {
      const components = [];
      for (let comp of component) {
        const code = `${comp.code.text}: `;
        const nodeValue = this.getNodeValue(comp);
        components.push(code + nodeValue);
      }
      return components.join(",");
    } else {
      return null;
    }
  }
  //valueDateTime //valuesampledData //valueAttachemnt(attachement) //ValueInteger  //ValueRange - range.low.value - high.value

  submitToCibmtr() {
    //reset
    this.selectedNewEntries = [];
    this.selectedUpdatedEntries = [];
    this.selectedNewResources = [];
    this.selectedUpdatedResources = [];

    // New Records
    this.selectedNewEntries.push(
      this.categoryData.filter((m) => m.selected === true && m.state === "bold")
    );

    this.selectedNewResources = Array.prototype.concat.apply(
      [],
      this.selectedNewEntries
    );

    this.selectedNewResources = Array.prototype.concat.apply(
      [],
      this.utility.buildSelectedResources(this.selectedNewResources)
    );

    // Updated Records
    this.selectedUpdatedEntries.push(
      this.categoryData.filter(
        (m) => m.selected === true && m.state === "normal"
      )
    );

    this.selectedUpdatedResources = Array.prototype.concat.apply(
      [],
      this.selectedUpdatedEntries
    );

    this.selectedUpdatedResources = Array.prototype.concat.apply(
      [],
      this.utility.buildSelectedResources(this.selectedUpdatedEntries)
    );

    const totalEntries = [
      ...this.selectedNewResources,
      ...this.selectedUpdatedResources,
    ];

    if (totalEntries && totalEntries.length > 0) {
      const bundles = this.observationlabsService.getBundles(
        totalEntries,
        this.psScope
      );

      let _successCount = 0;

      this.spinner.start();
      from(bundles)
        .pipe(
          mergeMap(
            (bundle) => this.observationlabsService.getBundleObservable(bundle),
            8  // limit number of concurrent bundle submissions
          ),
          finalize(() => {
            this.spinner.end();
            this.totalSuccessCount = _successCount;
            this.totalFailCount = totalEntries.length - _successCount;
            this.checkForSelectAll();
            //Send Message Header Bundle only upon successful Observations submission.
            if (this.isAllSelected && this.totalSuccessCount > 0 && this.totalFailCount === 0) {
              this.observationlabsService.submitMsgHeader(this.observationlabsService.getMsgHeaderBundle()).subscribe(
                (response: any) => {
                  this.appLogger.logInfo(response, { context: 'Submitting MsgHeader from Obs labs'});
                }
              );
            }
            if (this.totalFailCount > 0) {
              this.appLogger.logError(`Failed to submit ${this.totalFailCount} Obs labs to Cibmtr`, {context: `submitToCibmtr in Obs labs component`});
            }
          })
        )
        .subscribe(
          (response: any) => {
            response.entry &&
              response.entry.forEach((entry) => {
                const idValue = entry.resource.identifier[0].value;
                const matchedEntry = totalEntries.find((e) => {
                  return idValue === e.fullUrl;
                });
                matchedEntry.state = "lighter";
                _successCount++;
              });
            this.appLogger.logInfo(`Successfully submitted ${_successCount} Obs labs to Cibmtr`, {context: `submitToCibmtr in Obs labs component`, allowDuplicate: true });
          },
          (errorBundle) => {
            this.spinner.reset();
            this.appLogger.logError(errorBundle, { context: `Error in Submitting Obs labs to Cibmtr ` });
          }
        );
    }
  }

  checkForSelectAll() {
    this.isAllSelected = this.categoryData.every((entry) => entry.selected);
    this.isAlldisabled = this.categoryData.every(
      (entry) => entry.selected && entry.state === "lighter"
    );
  }

  selectAll() {
    const toggleStatus = this.isAllSelected;
    this.categoryData.forEach((entry) => {
      if (entry.state !== "lighter") {
        entry.selected = toggleStatus;
      }
    });
  }

  toggleOption = function () {
    this.isAllSelected = this.categoryData.every(function (entry) {
      return entry.selected;
    });
  };

  toggleAllOption = function () {
    this.isAlldisabled = this.categoryData.every(function (entry) {
      return entry.disabled;
    });
  };

  get toggleAlert() {
    return this.totalSuccessCount > 0 || this.totalFailCount > 0
      ? "show"
      : "hide";
  }

  getCategoryData() {
    if (this.route.snapshot.routeConfig.path === "priority") {
      return this.priority;
    }
    return this.labs;
  }

  sortHeader(event: Sort) {
    const isAsc = event.direction === "asc";

    if (!event.active || event.direction === "") {
      return this.categoryData;
    } else {
      this.categoryData.sort((a, b) => {
        switch (event.active) {
          case "time":
            return this.compare(
              a.resource.effectiveDateTime,
              b.resource.effectiveDateTime,
              isAsc
            );
          case "name":
            return this.compare(
              a.resource.code?.text,
              b.resource.code?.text,
              isAsc
            );
          default:
            return null;
        }
      });
    }
    return null;
  }

  compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
